import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sun, ZoomIn } from 'lucide-react';
import { api } from '@/utils/api';
import ChapterLoading from '@/components/capitulo_new/ChapterLoading';
import ChapterError from '@/components/capitulo_new/ChapterError';
import VerticalReader from '@/components/capitulo_new/VerticalReader';
import HorizontalReader, { type HorizontalReaderHandle } from '@/components/capitulo_new/HorizontalReader';
import ChapterNavbar from '@/components/capitulo_new/ChapterNavbar';
import ChapterBottomNavbar from '@/components/capitulo_new/ChapterBottomNavbar';
import { getCapNome, getCapNum } from '@/utils/capitulo-fields';
import { useOfflineChapters } from '@/hooks/useOfflineChapters';
import { chapterApiPayloadSupportsOfflineDownload, effectiveImagePageCountFromApiChapter } from '@/utils/offline-chapter-eligibility';
import ChapterTextContent from '@/components/capitulo/ChapterTextContent';
import ChapterInteractions from '@/components/capitulo_new/ChapterInteractions';
import { generateChapterLink } from '@/utils/utils';
import { addObraChapterToHistory } from '@/utils/history';
import { touchReadingTab } from '@/utils/reading-tabs';
import { useReadingTabs } from '@/hooks/useReadingTabs';
import ChapterReadingTabBar from '@/components/reading-tabs/ChapterReadingTabBar';

const INFINITE_SCROLL_KEY = 'chapter-infinite-scroll';
const DOUBLE_TAP_CONTROLS_KEY = 'chapter-double-tap-controls';
/** Formato Manga no manhwa-api — leitor horizontal com Swiper. */
const FORMATO_MANGA_ID = 13;

function sortChaptersByNum(list: any[]) {
    return [...list].sort((a, b) => {
        const an = parseFloat(String(a?.cap_num ?? a?.numero ?? 0)) || 0;
        const bn = parseFloat(String(b?.cap_num ?? b?.numero ?? 0)) || 0;
        return an - bn;
    });
}

function ChapterBetweenDivider({ cap }: { cap: any }) {
    const nome = getCapNome(cap);
    const num = getCapNum(cap);
    const label =
        nome && String(nome) !== String(num ?? '')
            ? String(nome)
            : num != null
              ? `Cap. ${num}`
              : 'Capítulo seguinte';

    return (
        <div
            className="w-full shrink-0 py-5 px-4 bg-gradient-to-b from-black via-zinc-950 to-black border-y border-white/[0.07]"
            role="separator"
            aria-label={label}
        >
            <div className="max-w-md mx-auto flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/35 to-transparent" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50 whitespace-nowrap px-2 py-1 rounded-full border border-white/10 bg-black/60">
                    {label}
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/35 to-transparent" />
            </div>
        </div>
    );
}

/** Modo de renderização para capítulos com `cap_conteudo_texto` (novel / texto). */
function resolveTextChapterRenderMode(cap: any): 'markdown' | 'html' | 'texto' {
    const t = String(cap?.cap_tipo ?? cap?.tipo ?? '').toLowerCase();
    if (t === 'markdown') return 'markdown';
    if (t === 'html') return 'html';
    return 'texto';
}

/** Navegação cap. anterior/seguinte no fim do scroll — só quando o scroll infinito está desligado (a barra flutuante mantém-se). */
function ChapterEndNavStrip({
    prevChapter,
    nextChapter,
    navigate,
}: {
    prevChapter: any;
    nextChapter: any;
    navigate: ReturnType<typeof useNavigate>;
}) {
    const go = (c: any) => {
        const id = c?.cap_id ?? c?.id;
        if (id == null) return;
        navigate(generateChapterLink(id));
    };
    return (
        <nav
            className="w-full shrink-0 border-t border-white/10 bg-gradient-to-b from-zinc-950/80 to-black px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5"
            aria-label="Capítulo anterior ou seguinte no fim da leitura"
        >
            <div className="mx-auto flex max-w-4xl items-center justify-center gap-3 sm:gap-4">
                <button
                    type="button"
                    disabled={!prevChapter}
                    onClick={() => prevChapter && go(prevChapter)}
                    title="Capítulo anterior"
                    className={`flex min-h-[48px] min-w-[48px] flex-1 max-w-[200px] items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors sm:flex-initial sm:px-6 ${
                        prevChapter
                            ? 'border-white/15 bg-white/[0.06] text-white hover:border-orange-500/40 hover:bg-orange-500/10'
                            : 'cursor-not-allowed border-white/5 bg-white/[0.02] text-white/25'
                    }`}
                >
                    <ChevronLeft size={22} className="shrink-0" aria-hidden />
                    <span className="truncate">Anterior</span>
                </button>
                <button
                    type="button"
                    disabled={!nextChapter}
                    onClick={() => nextChapter && go(nextChapter)}
                    title="Próximo capítulo"
                    className={`flex min-h-[48px] min-w-[48px] flex-1 max-w-[200px] items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors sm:flex-initial sm:px-6 ${
                        nextChapter
                            ? 'border-white/15 bg-white/[0.06] text-white hover:border-orange-500/40 hover:bg-orange-500/10'
                            : 'cursor-not-allowed border-white/5 bg-white/[0.02] text-white/25'
                    }`}
                >
                    <span className="truncate">Seguinte</span>
                    <ChevronRight size={22} className="shrink-0" aria-hidden />
                </button>
            </div>
        </nav>
    );
}

export default function CapituloPage() {
    const { id: chapterId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    /** [0] = capítulo da rota; demais = pré-carregados no scroll infinito */
    const [capitulos, setCapitulos] = useState<any[]>([]);
    const capitulosRef = useRef(capitulos);
    capitulosRef.current = capitulos;
    const [obra, setObra] = useState<any>(null);
    const [loadError, setLoadError] = useState<'vip' | 'general' | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [imageZoom, setImageZoom] = useState(100);
    const [imageBrightness, setImageBrightness] = useState(100);
    const [infiniteScroll, setInfiniteScroll] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem(INFINITE_SCROLL_KEY) === 'true';
    });
    const [loadingAppend, setLoadingAppend] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [doubleTapControlsOnly, setDoubleTapControlsOnly] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem(DOUBLE_TAP_CONTROLS_KEY) === 'true';
    });
    const [isDownloadingChapter, setIsDownloadingChapter] = useState(false);
    const infiniteSentinelRef = useRef<HTMLDivElement | null>(null);
    /** Evita várias chamadas antes do setState de loading; não depende de re-render */
    const appendInFlightRef = useRef(false);
    const infiniteScrollRef = useRef(infiniteScroll);
    infiniteScrollRef.current = infiniteScroll;
    const chapterIdRef = useRef(chapterId);
    chapterIdRef.current = chapterId;
    const lastReplacedChapterIdRef = useRef<string | null>(null);
    const orderedChaptersRef = useRef<any[]>([]);
    const lastScrollYRef = useRef(0);
    const lastTapAtRef = useRef(0);
    const { enqueueDownload } = useOfflineChapters();

    const [mangaPageInfo, setMangaPageInfo] = useState<{ current: number; total: number }>({ current: 1, total: 1 });
    const [visibleMangaCapId, setVisibleMangaCapId] = useState<number | null>(null);
    const mangaReaderRef = useRef<HorizontalReaderHandle | null>(null);
    const { isFullscreen, toggle: toggleFullscreen, isSupported: fullscreenSupported } = useFullscreen();
    const readingTabs = useReadingTabs();

    useEffect(() => {
        document.body.classList.add('capitulo-page');
        return () => document.body.classList.remove('capitulo-page');
    }, []);

    const getCapitulo = async (id: string) => {
        try {
            const res = await api.get(`capitulos/${id}`);
            return (res as any)?.data ?? res;
        } catch {
            return null;
        }
    };

    const getObra = async (id: string) => {
        try {
            const res = await api.get(`obras/${id}`);
            return (res as any)?.data ?? res;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        const handleGetCapitulo = async (id: string) => {
            if (!id) return;
            if (!infiniteScrollRef.current && typeof window !== 'undefined') {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
            setIsLoading(true);
            setLoadError(null);
            appendInFlightRef.current = false;
            setLoadingAppend(false);
            setCapitulos([]);
            try {
                const data = await getCapitulo(id);
                if (!data) {
                    setLoadError('general');
                    return;
                }
                setCapitulos([data]);
                const obraId = data.obra?.id ?? data.obra?.obr_id;
                const obraData = obraId != null ? await getObra(String(obraId)) : null;
                setObra(obraData);

                const ro = obraData ?? data.obra;
                const oid = Number(ro?.obr_id ?? ro?.id ?? obraId);
                const capId = Number(data.cap_id ?? data.id);
                if (Number.isFinite(oid) && oid > 0 && Number.isFinite(capId) && capId > 0) {
                    addObraChapterToHistory(
                        {
                            id: oid,
                            nome: ro?.obr_nome ?? ro?.nome,
                            imagem: ro?.obr_imagem ?? ro?.imagem,
                        },
                        {
                            id: capId,
                            numero: data.cap_num ?? data.numero,
                            nome: data.cap_nome ?? data.nome,
                        },
                    );
                    touchReadingTab(
                        {
                            id: oid,
                            nome: ro?.obr_nome ?? ro?.nome,
                            imagem: ro?.obr_imagem ?? ro?.imagem,
                        },
                        {
                            id: capId,
                            numero: data.cap_num ?? data.numero,
                            nome: data.cap_nome ?? data.nome,
                        },
                    );
                }
                if (!infiniteScrollRef.current && typeof window !== 'undefined') {
                    requestAnimationFrame(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    });
                }
            } catch {
                setLoadError('general');
            } finally {
                setIsLoading(false);
            }
        };
        const timeout = setTimeout(() => {
            handleGetCapitulo(chapterId ?? '');
        }, 100);
        return () => clearTimeout(timeout);
    }, [chapterId]);

    useEffect(() => {
        if (!infiniteScroll) {
            setCapitulos((prev) => (prev.length <= 1 ? prev : [prev[0]]));
        }
    }, [infiniteScroll]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(DOUBLE_TAP_CONTROLS_KEY, String(doubleTapControlsOnly));
        if (doubleTapControlsOnly) {
            setControlsVisible(false);
            return;
        }
        setControlsVisible(true);
    }, [doubleTapControlsOnly]);

    const capitulo = capitulos[0] ?? null;

    const chaptersList = useMemo(() => {
        const fromObra = Array.isArray(obra?.capitulos) && obra.capitulos.length > 0 ? obra.capitulos : [];
        if (fromObra.length > 0) return fromObra;
        return Array.isArray(capitulo?.obra?.capitulos) ? capitulo.obra.capitulos : [];
    }, [obra, capitulo]);

    const orderedChapters = useMemo(() => sortChaptersByNum(chaptersList), [chaptersList]);
    orderedChaptersRef.current = orderedChapters;

    const isMangaLayout = useMemo(() => {
        const fid =
            obra?.formt_id ??
            obra?.formato?.formt_id ??
            capitulo?.obra?.formt_id ??
            capitulo?.obra?.formato?.formt_id;
        return Number(fid) === FORMATO_MANGA_ID;
    }, [obra, capitulo]);

    const navCapitulo = useMemo(() => {
        if (!capitulo) return null;
        if (!isMangaLayout || !infiniteScroll || visibleMangaCapId == null) return capitulo;
        return capitulos.find((c) => Number(c.cap_id ?? c.id) === visibleMangaCapId) ?? capitulo;
    }, [capitulo, isMangaLayout, infiniteScroll, visibleMangaCapId, capitulos]);

    const mangaReaderProps = useMemo(() => {
        if (!capitulo || capitulo.cap_tipo !== 'imagem' || !isMangaLayout) return null;
        return {
            currentPage: mangaPageInfo.current,
            totalPages: mangaPageInfo.total,
            onPrevPage: () => mangaReaderRef.current?.slidePrev(),
            onNextPage: () => mangaReaderRef.current?.slideNext(),
        };
    }, [capitulo, isMangaLayout, mangaPageInfo]);

    const toggleInfiniteScroll = useCallback(() => {
        setInfiniteScroll((v) => {
            const next = !v;
            if (typeof window !== 'undefined') {
                localStorage.setItem(INFINITE_SCROLL_KEY, next.toString());
            }
            return next;
        });
    }, []);

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const toggleDoubleTapControlsOnly = useCallback(() => {
        setDoubleTapControlsOnly((value) => !value);
    }, []);

    const handleDownloadChapter = useCallback(async () => {
        const current = capitulosRef.current[0];
        if (!current || isDownloadingChapter) return;
        const capId = Number(current.cap_id ?? current.id);
        const obraId = Number(current?.obra?.id ?? current?.obra?.obr_id);
        if (!capId || !obraId) return;
        setIsDownloadingChapter(true);
        try {
            const res = await api.get(`capitulos/${capId}`);
            const data = (res as any)?.data ?? res;
            if (!chapterApiPayloadSupportsOfflineDownload(data)) {
                window.alert('Este capítulo não está disponível para download offline.');
                return;
            }
            const pageCount = Math.max(1, effectiveImagePageCountFromApiChapter(data));
            enqueueDownload({
                chapterId: capId,
                obraId,
                obraName: String(current?.obra?.nome ?? current?.obra?.obr_nome ?? ''),
                obraImage: current?.obra?.obr_imagem ?? current?.obra?.imagem ?? null,
                chapterNum: current.cap_num ?? current.numero ?? '',
                chapterName: String(getCapNome(current) ?? `Capítulo ${getCapNum(current) ?? ''}`),
                pageCount,
                savedAt: Date.now(),
            });
            window.alert('Capítulo adicionado à fila de download offline.');
        } catch (error) {
            console.error('Erro ao baixar capítulo:', error);
            window.alert('Não foi possível iniciar o download deste capítulo.');
        } finally {
            setIsDownloadingChapter(false);
        }
    }, [enqueueDownload, isDownloadingChapter]);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(() => {
                const y = window.scrollY;
                const delta = y - lastScrollYRef.current;
                if (Math.abs(delta) >= 10 && !doubleTapControlsOnly) {
                    if (delta > 0 && y > 24) setControlsVisible(false);
                    if (delta < 0) setControlsVisible(true);
                    if (y <= 0) setControlsVisible(true);
                }
                lastScrollYRef.current = y;
                ticking = false;
            });
        };
        lastScrollYRef.current = window.scrollY;
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [doubleTapControlsOnly]);

    const handleReaderPointerDown = useCallback(() => {
        if (!doubleTapControlsOnly) return;
        const now = Date.now();
        if (now - lastTapAtRef.current < 280) {
            setControlsVisible((value) => !value);
            lastTapAtRef.current = 0;
            return;
        }
        lastTapAtRef.current = now;
    }, [doubleTapControlsOnly]);

    /** Estável: lê tudo via ref para o IntersectionObserver não ser recriado a cada loading */
    const loadNextAppend = useCallback(async () => {
        if (appendInFlightRef.current) return;
        const first = capitulosRef.current[0];
        if (!infiniteScrollRef.current || !first) return;

        const firstIsImage = first.cap_tipo === 'imagem';

        const ordered = orderedChaptersRef.current;
        const loaded = capitulosRef.current;
        const lastLoaded = loaded[loaded.length - 1];
        const lastLoadedId = Number(lastLoaded?.cap_id ?? lastLoaded?.id ?? chapterIdRef.current);
        const idx = ordered.findIndex((c) => Number(c.cap_id ?? c.id) === lastLoadedId);
        if (idx < 0) return;

        const nextMeta = ordered[idx + 1];
        if (!nextMeta) return;
        const nextId = Number(nextMeta.cap_id ?? nextMeta.id);
        if (!nextId || loaded.some((c) => Number(c.cap_id ?? c.id) === nextId)) return;

        appendInFlightRef.current = true;
        setLoadingAppend(true);
        try {
            const res = await api.get(`capitulos/${nextId}`);
            const cap = (res as any)?.data ?? res;
            if (!cap) return;
            if (firstIsImage && cap.cap_tipo !== 'imagem') return;
            if (!firstIsImage && cap.cap_tipo === 'imagem') return;
            setCapitulos((prev) => {
                if (prev.some((c) => Number(c.cap_id ?? c.id) === nextId)) return prev;
                return [...prev, cap];
            });
        } catch {
            // ignora falha ao pré-carregar próximo capítulo
        } finally {
            appendInFlightRef.current = false;
            setLoadingAppend(false);
        }
    }, []);

    const maybeLoadNextIfNearBottom = useCallback(() => {
        if (!infiniteScrollRef.current) return;
        const el = infiniteSentinelRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 320) {
            void loadNextAppend();
        }
    }, [loadNextAppend]);

    const infiniteScrollActive = infiniteScroll && capitulos.length > 0;

    useEffect(() => {
        if (!infiniteScrollActive) return;
        const el = infiniteSentinelRef.current;
        if (!el) return;
        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        const io = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    void loadNextAppend();
                }, 200);
            },
            { root: null, rootMargin: '320px 0px', threshold: 0 }
        );
        io.observe(el);
        return () => {
            clearTimeout(debounceTimer);
            io.disconnect();
        };
    }, [infiniteScrollActive, capitulos[0]?.cap_tipo, orderedChapters.length, loadNextAppend]);

    useEffect(() => {
        if (!infiniteScrollActive) return;
        // Garante o start/restart do infinito quando capítulos/metadados entram depois.
        const timer = setTimeout(maybeLoadNextIfNearBottom, 120);
        return () => clearTimeout(timer);
    }, [
        infiniteScrollActive,
        capitulos[0]?.cap_tipo,
        capitulos.length,
        orderedChapters.length,
        maybeLoadNextIfNearBottom,
    ]);

    useEffect(() => {
        if (!isMangaLayout || capitulo?.cap_tipo !== 'imagem') return;
        const id = Number(capitulo?.cap_id ?? capitulo?.id);
        if (!Number.isFinite(id)) return;
        setVisibleMangaCapId(id);
    }, [isMangaLayout, capitulo?.cap_tipo, chapterId, infiniteScroll, capitulo]);

    useEffect(() => {
        if (!isMangaLayout || capitulo?.cap_tipo !== 'imagem' || !infiniteScroll) return;
        if (!visibleMangaCapId || !Number.isFinite(visibleMangaCapId)) return;
        const targetId = String(visibleMangaCapId);
        if (targetId === lastReplacedChapterIdRef.current) return;
        const nextUrl = generateChapterLink(visibleMangaCapId);
        if (typeof window !== 'undefined' && window.location.pathname !== nextUrl) {
            window.history.replaceState(window.history.state, '', nextUrl);
        }
        lastReplacedChapterIdRef.current = targetId;
    }, [isMangaLayout, capitulo?.cap_tipo, infiniteScroll, visibleMangaCapId]);

    useEffect(() => {
        if (!isMangaLayout || capitulo?.cap_tipo !== 'imagem' || !infiniteScroll) return;
        if (visibleMangaCapId == null || !Number.isFinite(visibleMangaCapId)) return;
        const cap = capitulos.find((c) => Number(c.cap_id ?? c.id) === visibleMangaCapId);
        if (!cap) return;
        const ro = cap.obra ?? obra;
        const oid = Number(ro?.obr_id ?? ro?.id ?? cap.obr_id);
        const cid = Number(cap.cap_id ?? cap.id);
        if (!Number.isFinite(oid) || oid < 1 || !Number.isFinite(cid) || cid < 1) return;
        touchReadingTab(
            { id: oid, nome: ro?.obr_nome ?? ro?.nome, imagem: ro?.obr_imagem ?? ro?.imagem },
            { id: cid, numero: cap.cap_num ?? cap.numero, nome: cap.cap_nome ?? cap.nome },
        );
    }, [isMangaLayout, infiniteScroll, visibleMangaCapId, capitulos, obra, capitulo?.cap_tipo]);

    const chapterKeyForArrows = useMemo(() => {
        if (isMangaLayout && infiniteScroll && visibleMangaCapId != null) {
            return String(visibleMangaCapId);
        }
        return String(chapterId ?? '');
    }, [isMangaLayout, infiniteScroll, visibleMangaCapId, chapterId]);

    const prevNextChapters = useMemo(() => {
        const ordered = sortChaptersByNum(chaptersList);
        const idx = ordered.findIndex((c) => String(c?.cap_id ?? c?.id ?? '') === chapterKeyForArrows);
        const prevChapter = idx > 0 ? ordered[idx - 1] : null;
        const nextChapter = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;
        return { prevChapter, nextChapter };
    }, [chaptersList, chapterKeyForArrows]);

    const showDesktopReaderHud = useMemo(() => {
        if (!capitulo) return false;
        const txt = String(capitulo.cap_conteudo_texto ?? capitulo.conteudo_texto ?? '');
        return capitulo.cap_tipo === 'imagem' || (capitulo.cap_tipo !== 'imagem' && txt.trim().length > 0);
    }, [capitulo]);

    useEffect(() => {
        const isDesktop = () =>
            typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

        const onKeyDown = (e: KeyboardEvent) => {
            if (!isDesktop() || !capitulo) return;
            if (e.altKey || e.ctrlKey || e.metaKey) return;
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

            const t = e.target;
            if (t instanceof HTMLElement) {
                if (t.closest('input, textarea, select, [contenteditable="true"]')) return;
            }

            const goPrev = () => {
                const p = prevNextChapters.prevChapter;
                if (!p) return false;
                const id = p.cap_id ?? p.id;
                if (id == null) return false;
                navigate(generateChapterLink(id));
                return true;
            };
            const goNext = () => {
                const n = prevNextChapters.nextChapter;
                if (!n) return false;
                const id = n.cap_id ?? n.id;
                if (id == null) return false;
                navigate(generateChapterLink(id));
                return true;
            };

            const imagem = capitulo.cap_tipo === 'imagem';

            if (imagem && isMangaLayout) {
                const { current, total } = mangaPageInfo;
                if (e.key === 'ArrowLeft') {
                    if (current <= 1 && goPrev()) e.preventDefault();
                    return;
                }
                if (e.key === 'ArrowRight') {
                    if (current >= total && goNext()) e.preventDefault();
                    return;
                }
                return;
            }

            if (imagem && !isMangaLayout) {
                if (e.key === 'ArrowLeft') {
                    if (goPrev()) e.preventDefault();
                } else if (goNext()) {
                    e.preventDefault();
                }
                return;
            }

            if (e.key === 'ArrowLeft') {
                if (goPrev()) e.preventDefault();
            } else if (goNext()) {
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [capitulo, isMangaLayout, mangaPageInfo, navigate, prevNextChapters]);

    const hasReadingTabStripMd = readingTabs.length > 0;
    const activeReadingTabCapId = useMemo(() => {
        if (isMangaLayout && infiniteScroll && visibleMangaCapId != null) return visibleMangaCapId;
        const n = Number(chapterId);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [isMangaLayout, infiniteScroll, visibleMangaCapId, chapterId]);

    const mainReaderClass = useMemo(
        () =>
            'max-w-4xl mx-auto min-h-screen bg-black pt-14 pb-32' +
            (hasReadingTabStripMd ? ' md:pt-[5.75rem]' : '') +
            (showDesktopReaderHud ? ' md:pr-40' : ''),
        [showDesktopReaderHud, hasReadingTabStripMd]
    );

    if (!chapterId) return null;
    if (isLoading) return <ChapterLoading />;
    if (loadError) return <ChapterError type={loadError} onBack={() => navigate(-1)} />;
    if (!capitulo) return <ChapterError type="general" onBack={() => navigate(-1)} />;

    const textoCapitulo = String(capitulo.cap_conteudo_texto ?? capitulo.conteudo_texto ?? '');

    const capituloNav = navCapitulo ?? capitulo;

    const chapterEndNavBeforeReactions =
        !infiniteScroll ? (
            <ChapterEndNavStrip
                prevChapter={prevNextChapters.prevChapter}
                nextChapter={prevNextChapters.nextChapter}
                navigate={navigate}
            />
        ) : null;

    return (
        <div className="capitulo-page-root min-h-screen bg-black text-white" onPointerDown={handleReaderPointerDown}>
            <ChapterReadingTabBar tabs={readingTabs} activeCapId={activeReadingTabCapId} />
            <ChapterNavbar
                capitulo={capituloNav}
                visible={controlsVisible}
                infiniteScroll={infiniteScroll}
                onToggleInfiniteScroll={toggleInfiniteScroll}
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
                showFullscreen={fullscreenSupported}
                stackedReadingTabs={hasReadingTabStripMd}
            />
            <main className={mainReaderClass}>
                {capitulo.cap_tipo === 'imagem' ? (
                    <>
                        {isMangaLayout ? (
                            <section className="w-full flex flex-col gap-0" aria-label="Leitor Manga">
                                <HorizontalReader
                                    ref={mangaReaderRef}
                                    capitulo={capitulos[0]}
                                    capitulos={capitulos}
                                    imageZoom={imageZoom}
                                    imageBrightness={imageBrightness}
                                    infiniteScroll={infiniteScroll}
                                    hasNextChapterInObra={(() => {
                                        const lastLoaded = capitulos[capitulos.length - 1];
                                        const lastLoadedId = Number(lastLoaded?.cap_id ?? lastLoaded?.id);
                                        const idxInOrdered = orderedChapters.findIndex(
                                            (c) => Number(c.cap_id ?? c.id) === lastLoadedId
                                        );
                                        return idxInOrdered >= 0 && idxInOrdered < orderedChapters.length - 1;
                                    })()}
                                    nextChapterName={(() => {
                                        const lastLoaded = capitulos[capitulos.length - 1];
                                        const lastLoadedId = Number(lastLoaded?.cap_id ?? lastLoaded?.id);
                                        const idxInOrdered = orderedChapters.findIndex(
                                            (c) => Number(c.cap_id ?? c.id) === lastLoadedId
                                        );
                                        if (idxInOrdered < 0) return null;
                                        const nextMeta = orderedChapters[idxInOrdered + 1];
                                        if (!nextMeta) return null;
                                        return String(
                                            nextMeta?.cap_nome ??
                                                nextMeta?.nome ??
                                                (nextMeta?.cap_num ?? nextMeta?.numero) ??
                                                ''
                                        );
                                    })()}
                                    loadingAppendNext={loadingAppend}
                                    onRequestLoadNextChapter={loadNextAppend}
                                    onPageChange={(cur, tot) => setMangaPageInfo({ current: cur, total: tot })}
                                    onActiveChapterChange={(capIdNum: number) => setVisibleMangaCapId(capIdNum)}
                                    beforeInteractions={chapterEndNavBeforeReactions}
                                />
                            </section>
                        ) : (
                            capitulos.map((cap, i) => (
                                <Fragment key={Number(cap.cap_id ?? cap.id) || i}>
                                    {i > 0 ? <ChapterBetweenDivider cap={cap} /> : null}
                                    <section className="w-full flex flex-col gap-0" aria-label={`Capítulo ${i + 1}`}>
                                        <VerticalReader
                                            capitulo={cap}
                                            imageZoom={imageZoom}
                                            imageBrightness={imageBrightness}
                                            infiniteScroll={infiniteScroll}
                                            beforeInteractions={chapterEndNavBeforeReactions}
                                        />
                                    </section>
                                </Fragment>
                            ))
                        )}
                        {infiniteScroll && !isMangaLayout && (
                            <div
                                ref={infiniteSentinelRef}
                                className="w-full h-24 flex items-center justify-center text-white/30 text-xs"
                                aria-hidden
                            >
                                {loadingAppend ? (
                                    <span className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                                ) : null}
                            </div>
                        )}
                    </>
                ) : capitulo.cap_tipo !== 'imagem' ? (
                    !textoCapitulo.trim() && capitulos.length <= 1 ? (
                        <>
                            <div className="min-h-[40vh] flex items-center justify-center px-6 text-center text-white/55 text-sm">
                                Este capítulo ainda não tem conteúdo de texto.
                            </div>
                            {chapterEndNavBeforeReactions}
                            {infiniteScroll && (
                                <div
                                    ref={infiniteSentinelRef}
                                    className="w-full h-24 flex items-center justify-center text-white/30 text-xs"
                                    aria-hidden
                                >
                                    {loadingAppend ? (
                                        <span className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                                    ) : null}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {capitulos.map((cap, i) => {
                                const texto = String(cap.cap_conteudo_texto ?? cap.conteudo_texto ?? '');
                                const mode = resolveTextChapterRenderMode(cap);
                                return (
                                    <Fragment key={Number(cap.cap_id ?? cap.id) || i}>
                                        {i > 0 ? <ChapterBetweenDivider cap={cap} /> : null}
                                        <section
                                            className="w-full px-4 sm:px-6 flex flex-col"
                                            aria-label={`Capítulo ${i + 1}`}
                                        >
                                            {!texto.trim() ? (
                                                <div className="min-h-[24vh] flex items-center justify-center px-2 text-center text-white/45 text-sm">
                                                    Sem conteúdo de texto neste capítulo.
                                                </div>
                                            ) : (
                                                <ChapterTextContent
                                                    tipo={mode}
                                                    conteudo={texto}
                                                    zoom={imageZoom}
                                                    brightness={imageBrightness}
                                                />
                                            )}
                                            {chapterEndNavBeforeReactions}
                                            <ChapterInteractions capitulo={cap} infiniteScroll={infiniteScroll} />
                                        </section>
                                    </Fragment>
                                );
                            })}
                            {infiniteScroll && (
                                <div
                                    ref={infiniteSentinelRef}
                                    className="w-full h-24 flex items-center justify-center text-white/30 text-xs"
                                    aria-hidden
                                >
                                    {loadingAppend ? (
                                        <span className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                                    ) : null}
                                </div>
                            )}
                        </>
                    )
                ) : (
                    <div className="min-h-[40vh] flex items-center justify-center px-6 text-center text-white/55 text-sm">
                        Este capítulo não está disponível.
                    </div>
                )}
            </main>
            {showDesktopReaderHud ? (
                <aside
                    className={`hidden md:block fixed right-4 z-30 w-[11rem] select-none transition-all duration-300 ${
                        hasReadingTabStripMd ? 'top-28' : 'top-20'
                    } ${
                        controlsVisible
                            ? 'translate-x-0 opacity-100'
                            : 'translate-x-8 opacity-0 pointer-events-none'
                    }`}
                    aria-label="Atalhos e ajustes do leitor"
                >
                    <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-zinc-950/90 backdrop-blur-xl px-3 py-3 shadow-2xl">
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 text-[11px] text-white/75">
                                <ZoomIn size={13} className="text-orange-400/90 shrink-0" aria-hidden />
                                <span className="uppercase tracking-wide">Zoom</span>
                                <span className="ml-auto font-mono text-orange-400/90 tabular-nums">{imageZoom}%</span>
                            </div>
                            <input
                                type="range"
                                min={50}
                                max={120}
                                step={1}
                                value={imageZoom}
                                onChange={(e) => setImageZoom(Number(e.target.value))}
                                className="w-full accent-orange-500 h-1.5"
                                aria-label="Zoom das imagens"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 text-[11px] text-white/75">
                                <Sun size={13} className="text-orange-400/90 shrink-0" aria-hidden />
                                <span className="uppercase tracking-wide">Brilho</span>
                                <span className="ml-auto font-mono text-orange-400/90 tabular-nums">{imageBrightness}%</span>
                            </div>
                            <input
                                type="range"
                                min={40}
                                max={140}
                                step={1}
                                value={imageBrightness}
                                onChange={(e) => setImageBrightness(Number(e.target.value))}
                                className="w-full accent-orange-500 h-1.5"
                                aria-label="Brilho das imagens"
                            />
                        </div>
                        <p className="text-[10px] leading-snug text-white/50 border-t border-white/10 pt-2.5">
                            {isMangaLayout && capitulo.cap_tipo === 'imagem' ? (
                                <>
                                    Setas ← → mudam de página. Na primeira ou na última página deste trecho, passam
                                    para o capítulo anterior ou seguinte.
                                </>
                            ) : (
                                <>
                                    Teclado: ← capítulo anterior · → capítulo seguinte (só em desktop).
                                </>
                            )}
                        </p>
                    </div>
                </aside>
            ) : null}
            <ChapterBottomNavbar
                capitulo={capituloNav}
                chapters={chaptersList}
                routeChapterId={chapterId}
                navigationChapterId={
                    isMangaLayout && infiniteScroll && visibleMangaCapId != null
                        ? String(visibleMangaCapId)
                        : null
                }
                imageZoom={imageZoom}
                onImageZoomChange={setImageZoom}
                imageBrightness={imageBrightness}
                onImageBrightnessChange={setImageBrightness}
                infiniteScroll={infiniteScroll}
                onToggleInfiniteScroll={toggleInfiniteScroll}
                onScrollToTop={scrollToTop}
                controlsVisible={controlsVisible}
                doubleTapControlsOnly={doubleTapControlsOnly}
                onToggleDoubleTapControlsOnly={toggleDoubleTapControlsOnly}
                onDownloadChapter={handleDownloadChapter}
                isDownloadingChapter={isDownloadingChapter}
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
                showFullscreen={fullscreenSupported}
                mangaReader={mangaReaderProps}
            />
        </div>
    );
}
